// "Your last round" — the popup after Process closes a P&P round (Fernando, 2026-09-28):
// the speed, big and coloured like the statistics, the delta against the person's previous
// round, picking time, packing time, orders, who picked and packed. "See the steps" opens
// the round's timeline. Data: GET /api/pp/rounds/{id}/recap (pp_tracking.round_delta with
// nothing left to do).
import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { fmtDur } from '../statistics/shared';
import RoundDetail from '../statistics/RoundDetail';
import { Delta, speedCls, deltaCls, deltaArrow, fmtDelta } from './SpeedChip';

export default function RoundRecap({ roundId, onClose }: { roundId: number; onClose: () => void }) {
  const [d, setD] = useState<Delta | null>(null);
  const [error, setError] = useState('');
  const [steps, setSteps] = useState(false);
  useEffect(() => {
    authFetch(`${API}/pp/rounds/${roundId}/recap`).then(async r => {
      if (!r.ok) { setError((await r.json().catch(() => ({} as any))).detail || 'Could not load the round'); return; }
      setD(await r.json());
    }).catch(() => setError('Could not load the round'));
  }, [roundId]);
  const name = (u: string | null | undefined) => (u && d?.names?.[u]) || u || '—';
  const ref = d?.ref;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-2xl w-full max-w-md p-6 text-white" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-bold text-lg">Your last round</h2>
          <span className="text-xs text-gray-500">#{roundId}{d?.date ? ` · ${d.date}` : ''}</span>
        </div>
        {error && <div className="text-red-400 text-sm">{error}</div>}
        {!d && !error && <div className="text-gray-500 text-sm py-6 text-center">Loading…</div>}
        {d && (
          <>
            <div className="flex items-baseline gap-3">
              <span className={`text-6xl font-black ${speedCls(d.expected_oph, 'dark')}`}>{d.expected_oph != null ? d.expected_oph.toFixed(1) : '—'}</span>
              <span className="text-gray-400">orders / hour</span>
            </div>
            <div className={`mt-1 mb-5 font-semibold ${deltaCls(d.delta_sign, 'dark')}`}>
              {ref && d.delta != null
                ? `${deltaArrow(d.delta_sign)} ${fmtDelta(d.delta)} ${d.delta_sign === 'down' ? 'slower' : d.delta_sign === 'up' ? 'faster' : 'level'} — your previous round was ${ref.oph.toFixed(1)}/h (${ref.date}, ${ref.orders} orders)`
                : 'Your first round with a speed — the next one is compared with this'}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Orders packed" value={String(d.orders ?? 0)} />
              <Stat label="Whole round" value={fmtDur(d.elapsed_min ?? 0)} />
              <Stat label="Picking" value={d.picking_min != null ? fmtDur(d.picking_min) : '—'}
                    sub={d.sec_per_stop != null ? `${d.sec_per_stop} s/stop${ref ? ` · last ${ref.sec_per_stop}` : ''}` : undefined} />
              <Stat label="Packing" value={d.packing_min != null ? fmtDur(d.packing_min) : '—'}
                    sub={d.sec_per_order != null ? `${d.sec_per_order} s/order${ref ? ` · last ${ref.sec_per_order}` : ''}` : undefined} />
              <Stat label="Picked by" value={name(d.picker)} />
              <Stat label="Packed by" value={(d.packers || '').split(', ').filter(Boolean).map(name).join(', ') || '—'} />
            </div>
            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setSteps(true)} className="text-sm text-blue-300 hover:text-blue-200">See the steps →</button>
              <button onClick={onClose} className="px-5 py-2 rounded-lg bg-white text-black font-semibold text-sm">OK</button>
            </div>
          </>
        )}
      </div>
      {steps && <RoundDetail roundId={roundId} onClose={() => setSteps(false)} />}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-[#0f1117] border border-gray-800 rounded-xl px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}
